import { Chip } from '@heroui/react'

interface TokenUsageInfo {
  prompt_tokens?: number
  completion_tokens?: number
  input_tokens?: number
  output_tokens?: number
  total_tokens?: number
  estimated_cost_usd: {
    input: string
    output: string
    total: string
  }
  model: string
}

interface TimingBreakdown {
  documentFetchMs: number
  aiProcessingMs: number
}

interface PerformanceMetricsProps {
  // Performance data
  processingTimeMs?: number
  timingBreakdown?: TimingBreakdown
  tokenUsage?: {
    processing?: TokenUsageInfo
  }

  // Processing metrics
  documentsEvaluated?: number
  finalRecommendationCount?: number
  candidatesFound?: number

  className?: string
}

export function PerformanceMetrics({
  processingTimeMs,
  timingBreakdown,
  tokenUsage,
  documentsEvaluated,
  finalRecommendationCount,
  candidatesFound,
  className,
}: PerformanceMetricsProps) {
  const processing = tokenUsage?.processing

  return (
    <div className={`space-y-4 ${className || ''}`}>
      {/* Overall Performance Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {processingTimeMs !== undefined && (
          <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <div className="text-xs text-slate-500 mb-1">Total Time</div>
            <div className="text-lg font-bold text-blue-600">
              {processingTimeMs}ms
            </div>
          </div>
        )}

        {documentsEvaluated !== undefined && (
          <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <div className="text-xs text-slate-500 mb-1">Documents</div>
            <div className="text-lg font-bold text-green-600">
              {documentsEvaluated.toLocaleString()}
            </div>
          </div>
        )}

        {finalRecommendationCount !== undefined && (
          <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <div className="text-xs text-slate-500 mb-1">Results</div>
            <div className="text-lg font-bold text-purple-600">
              {finalRecommendationCount}
            </div>
          </div>
        )}

        {processing?.estimated_cost_usd?.total && (
          <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <div className="text-xs text-slate-500 mb-1">Cost</div>
            <div className="text-lg font-bold text-emerald-600">
              ${processing.estimated_cost_usd.total}
            </div>
          </div>
        )}
      </div>

      {/* Timing Breakdown */}
      {timingBreakdown && (
        <div>
          <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Timing Breakdown
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="text-xs text-blue-600 dark:text-blue-400 mb-1">
                Document Fetch
              </div>
              <div className="text-sm font-mono font-semibold">
                {timingBreakdown.documentFetchMs}ms
              </div>
            </div>
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <div className="text-xs text-purple-600 dark:text-purple-400 mb-1">
                AI Processing
              </div>
              <div className="text-sm font-mono font-semibold">
                {timingBreakdown.aiProcessingMs}ms
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Token Usage Details */}
      {processing && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Token Usage
            </h4>
            <Chip size="sm" variant="flat" color="secondary">
              {processing.model}
            </Chip>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="text-xs text-slate-500 mb-1">Input Tokens</div>
              <div className="text-sm font-mono font-semibold">
                {(
                  processing.input_tokens ||
                  processing.prompt_tokens ||
                  0
                ).toLocaleString()}
              </div>
              <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                ${processing.estimated_cost_usd.input}
              </div>
            </div>

            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="text-xs text-slate-500 mb-1">Output Tokens</div>
              <div className="text-sm font-mono font-semibold">
                {(
                  processing.output_tokens ||
                  processing.completion_tokens ||
                  0
                ).toLocaleString()}
              </div>
              <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                ${processing.estimated_cost_usd.output}
              </div>
            </div>

            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg md:col-span-1 col-span-2">
              <div className="text-xs text-emerald-600 dark:text-emerald-400 mb-1">
                Total Cost
              </div>
              <div className="text-lg font-mono font-bold text-emerald-600 dark:text-emerald-400">
                ${processing.estimated_cost_usd.total}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Additional Processing Metrics */}
      {candidatesFound !== undefined && (
        <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
          <div className="text-xs text-slate-500 mb-1">
            Search Candidates Found
          </div>
          <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">
            {candidatesFound.toLocaleString()}
          </div>
        </div>
      )}
    </div>
  )
}
